import { ZodError } from "zod";

import { dayjs } from "@holibob-packages/dayjs";

import { orderByDuration } from "./orderByDuration";
import {
    CancellationPenaltyAbsolute,
    CancellationPenaltyPercentage,
    ProductCancellationPenaltyInputData,
} from "./types";

const validateSchema = (cancellationPenalty: ProductCancellationPenaltyInputData, idx: number) => {
    const { type, duration } = cancellationPenalty;

    const errors: string[] = [];

    if (duration) {
        const valid = dayjs.duration(duration).toISOString() === duration;
        if (!valid) errors.push(`dayJSError:element[${idx}]:duration:invalidDuration`);
    }

    try {
        if (type === "ABSOLUTE") CancellationPenaltyAbsolute.parse(cancellationPenalty);
        else CancellationPenaltyPercentage.parse(cancellationPenalty);
    } catch (error) {
        const zodError = error as ZodError;
        errors.push(...zodError.errors.map((error) => `ZodError:element[${idx}]:${error.path[0]}:${error.message}`));
    }

    return errors;
};

const validateSame = (
    cancellationPolicy: ProductCancellationPenaltyInputData[],
    fieldName: keyof ProductCancellationPenaltyInputData
) => {
    const fields = cancellationPolicy.map((cancellationPenalty) => cancellationPenalty[fieldName]);
    return fields.every((field) => field === fields[0]);
};

const validateUnique = (
    cancellationPolicy: ProductCancellationPenaltyInputData[],
    fieldName: keyof ProductCancellationPenaltyInputData
) => {
    const fields = cancellationPolicy.map((cancellationPenalty) => cancellationPenalty[fieldName]);
    const fieldSet = new Set(fields);
    return fieldSet.size === fields.length;
};

const validateNoP0DDuration = (cancellationPolicy: ProductCancellationPenaltyInputData[]) => {
    const durations = cancellationPolicy.map((cancellationPenalty) => cancellationPenalty.duration);
    const p0DDurations = durations.filter((duration) => duration === "P0D");
    return p0DDurations.length === 0;
};

const validateNoDurationsOver6Months = (cancellationPolicy: ProductCancellationPenaltyInputData[]) => {
    const durations = cancellationPolicy.map((cancellationPenalty) => cancellationPenalty.duration);
    const durationsOver6Months = durations.filter((duration) =>
        duration ? dayjs.duration(duration).asSeconds() > dayjs.duration({ months: 6 }).asSeconds() : false
    );
    return durationsOver6Months.length === 0;
};

const validateExactly1NullDuration = (cancellationPolicy: ProductCancellationPenaltyInputData[]) => {
    const nullDurations = cancellationPolicy
        .map((cancellationPenalty) => cancellationPenalty.duration)
        .filter((duration) => duration === null);
    return nullDurations.length === 1;
};

const validateDirection = (
    cancellationPolicy: ProductCancellationPenaltyInputData[],
    fieldName: keyof ProductCancellationPenaltyInputData,
    type: "INCREASE" | "DECREASE"
) => {
    if (cancellationPolicy.length < 2) return true;
    const fields = cancellationPolicy.map((cancellationPenalty) => cancellationPenalty[fieldName]);
    const match = type === "INCREASE" ? 1 : -1;
    return isSorted(fields) === match;
};

const isSorted = (arr: number[]): number => {
    if (arr.length <= 1) return 0;
    const direction = arr[1]! - arr[0]!;
    for (let i = 2; i < arr.length; i++) {
        if ((arr[i]! - arr[i - 1]!) * direction < 0) return 0;
    }

    return Math.sign(direction);
};

const generateResponse = (errors: string[]) => ({
    isValid: errors.length === 0,
    errors,
    errorMessage: errors.join(", "),
});

export const validateCancellationPolicy = (cancellationPolicy: ProductCancellationPenaltyInputData[]) => {
    // 1. schema errors
    const errors = cancellationPolicy.flatMap((cancellationPenalty, idx) => validateSchema(cancellationPenalty, idx));
    if (errors.length) return generateResponse(errors);

    const firstCancellationPenalty = cancellationPolicy[0];
    if (firstCancellationPenalty) {
        // 2. general errors
        if (!validateSame(cancellationPolicy, "type")) errors.push("same:type");
        if (!validateSame(cancellationPolicy, "relativeTo")) errors.push("same:relativeTo");
        if (!validateUnique(cancellationPolicy, "duration")) errors.push("unique:duration");
        if (!validateExactly1NullDuration(cancellationPolicy)) errors.push("exactly1NullDuration");
        if (!validateNoP0DDuration(cancellationPolicy)) errors.push("noP0DDuration");
        if (!validateNoDurationsOver6Months(cancellationPolicy)) errors.push("noDurationOver6Months");
        if (errors.length) return generateResponse(errors);

        const { type, relativeTo, amountType } = firstCancellationPenalty;

        // 3. absolute errors
        if (type === "ABSOLUTE") {
            if (!validateSame(cancellationPolicy, "amountCurrency")) errors.push("same:amountCurrency");
            if (!validateSame(cancellationPolicy, "amountType")) errors.push("same:amountType");
            if (errors.length) return generateResponse(errors);
        }

        // 4. percentage errors
        if (type === "PERCENTAGE") {
            if (!validateUnique(cancellationPolicy, "refundPercentage")) errors.push("unique:refundPercentage");
            if (errors.length) return generateResponse(errors);
        }

        // 5. order errors
        const _cancellationPolicy = orderByDuration(cancellationPolicy, "asc");
        if (relativeTo === "TRAVEL_DATE") {
            if (type === "PERCENTAGE") {
                if (!validateDirection(_cancellationPolicy, "refundPercentage", "INCREASE"))
                    errors.push("increase:refundPercentage");
            } else if (type === "ABSOLUTE") {
                if (amountType === "REFUND") {
                    if (!validateDirection(_cancellationPolicy, "amount", "INCREASE")) errors.push("increase:amount");
                } else if (amountType === "CHARGE") {
                    if (!validateDirection(_cancellationPolicy, "amount", "DECREASE")) errors.push("decrease:amount");
                }
            }
        }

        if (relativeTo === "BOOKING_DATE") {
            if (type === "PERCENTAGE") {
                if (!validateDirection(_cancellationPolicy, "refundPercentage", "DECREASE"))
                    errors.push("decrease:refundPercentage");
            } else if (type === "ABSOLUTE") {
                if (amountType === "REFUND") {
                    if (!validateDirection(_cancellationPolicy, "amount", "DECREASE")) errors.push("decrease:amount");
                } else if (amountType === "CHARGE") {
                    if (!validateDirection(_cancellationPolicy, "amount", "INCREASE")) errors.push("increase:amount");
                }
            }
        }
    }

    return generateResponse(errors);
};
